/**
 * Phase 10d — Bloc Settings "Rappels internes".
 *
 * Séparé du bloc `RemindersSettingsBlock` (relances IA client-facing devis/factures) car ce flux
 * est purement interne : digest quotidien 08:00 Europe/Brussels envoyé à l'équipe (admins + secrétaires).
 *
 * Toggle unique : `supplier_reminders_enabled` (défaut ON côté backend).
 * Persistance via `PATCH /api/company/settings` (identique aux autres blocs Settings).
 */
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function InternalRemindersSettingsBlock({ form, setForm, onSaved }) {
  const [saving, setSaving] = useState(false);

  const saveField = async (field, value) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/company/settings", { [field]: value });
      setForm({ ...form, [field]: value });
      toast.success(
        `Rappels échéance factures fournisseurs ${value ? "activés" : "désactivés"}`
      );
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      id="internal-reminders"
      className="scroll-mt-24 border-slate-800 bg-slate-900/40"
      data-testid="internal-reminders-settings-card"
    >
      <CardContent className="space-y-4 p-6">
        <div className="flex items-start gap-2">
          <Bell className="mt-0.5 h-5 w-5 text-[#3498DB]" />
          <div>
            <h3 className="font-semibold text-slate-100">Rappels internes</h3>
            <p className="text-xs text-slate-400">
              Notifications envoyées à ton équipe (admins + secrétaires) pour ne pas rater
              d&apos;échéance. Ces emails ne sont <b>jamais</b> transmis aux clients ni aux
              fournisseurs.
            </p>
          </div>
        </div>

        {/* Toggle FACTURES FOURNISSEURS */}
        <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Label className="text-slate-200 font-semibold">
                Rappels échéance factures fournisseurs
              </Label>
              <p className="mt-1 text-xs text-slate-400">
                Digest quotidien à <b>08:00 (Europe/Brussels)</b> listant les factures
                fournisseurs <b>validées</b> avec 3 groupes : <b>en retard</b>, <b>J-1</b>,{" "}
                <b>J-5</b>. Un seul email par jour par entreprise, envoyé aux admins et
                secrétaires actifs.
              </p>
              <div className="mt-2 flex items-start gap-1.5 rounded border border-slate-800/70 bg-slate-950/40 px-2 py-1.5">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                <p className="text-xs text-slate-400">
                  Aucune donnée de marge n&apos;est incluse dans ces emails : uniquement
                  fournisseur, N° facture, montant TVAC et date d&apos;échéance.
                </p>
              </div>
            </div>
            <Switch
              checked={!!form.supplier_reminders_enabled}
              onCheckedChange={(v) => saveField("supplier_reminders_enabled", v)}
              disabled={saving}
              data-testid="supplier-reminders-toggle"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
